import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M20 L20.02 — Integration partner detail.
 *
 * Reads `GET /v1/integration/endpoints/:id` and renders the connection
 * configuration for a single external partner.
 */
interface PartnerDetail {
  readonly endpointId: string;
  readonly endpointName: string;
  readonly integrationType: string;
  readonly provider: string;
  readonly connectionMode: string;
  readonly baseUrl: string | null;
  readonly authType: string | null;
  readonly status: string;
  readonly lastHealthCheckAt: string | null;
}

function Field({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--color-bg-muted)' }}>
      <span style={{ width: 180, color: 'var(--color-text-muted)', fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13 }}>{value ?? '—'}</span>
    </div>
  );
}

export function IntegrationPartnerDetailPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [row, setRow] = useState<PartnerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRow(null);
    setError(null);
    apiRequest<{ data: PartnerDetail }>(`/v1/integration/endpoints/${encodeURIComponent(id ?? '')}`, { token })
      .then((r) => { if (alive) setRow(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, id]);

  return (
    <main aria-label="partner-detail">
      <Link to="/admin/integration/partners" data-testid="back-partners" style={{ fontSize: 13 }}>{t('m20.partnerDetail.back')}</Link>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!row ? (
        !error && <p>{t('m20.partnerDetail.loading')}</p>
      ) : (
        <>
          <h1 style={{ fontSize: 20, margin: '12px 0 16px' }}>{row.endpointName}</h1>
          <section aria-label="partner-fields" data-testid={`partner-${row.endpointId}`}>
            <Field label={t('m20.partnerDetail.type')} value={row.integrationType} />
            <Field label={t('m20.partnerDetail.provider')} value={row.provider} />
            <Field label={t('m20.partnerDetail.connectionMode')} value={row.connectionMode} />
            <Field label={t('m20.partnerDetail.baseUrl')} value={row.baseUrl} />
            <Field label={t('m20.partnerDetail.authType')} value={row.authType} />
            <Field label={t('m20.partnerDetail.status')} value={row.status} />
            <Field label={t('m20.partnerDetail.lastHealthCheck')} value={row.lastHealthCheckAt} />
          </section>
        </>
      )}
    </main>
  );
}
